import React, { useState, useEffect } from 'react'
import { FaPlus, FaCheckCircle, FaTrash } from 'react-icons/fa'
import { Box, Stack, SimpleModal, Button, Input, Text, Heading, ConfirmButton, Icon } from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'

const groupColours = ['#f5b7b1', '#d7bde2', '#a9cce3', '#a3e4d7', '#a9dfbf', '#f9e79f', '#f5cba7']

export default function GroupCreator ({ trigger, trial, group, onCreated, onUpdated, onDeleted }) {
  const [open, setOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState('')
  const [colour, setColour] = useState('#f5b7b1')
  const { addGroup, deleteGroup } = useStore()
  const groupId = group?._id

  useEffect(() => {
    if (group?._id) {
      setName(group.name)
      setColour(group.colour)
      setIsEditing(true)
    }
  }, [group, groupId])

  function createGroup () {
    if (!name || !colour) return
    const data = { name, colour }
    if (isEditing) {
      api.groups.update(group._id, data, newGroup => {
        utils.toast('success', 'Group saved')
        addGroup(newGroup)
        onUpdated && onUpdated(newGroup)
        setOpen(false)
      }, err => utils.toast('error', 'Unable to save group', err.message))
    } else {
      api.trials.createGroup(trial._id, data, newGroup => {
        utils.toast('success', 'Group created')
        addGroup(newGroup)
        onCreated && onCreated(newGroup)
        setOpen(false)
      }, err => utils.toast('error', 'Unable to create group', err.message))
    }
  }

  function onDeleteGroup (id) {
    api.groups.delete(id, () => {
      utils.toast('info', 'Group deleted')
      deleteGroup(id)
      onDeleted && onDeleted(id)
      setOpen(false)
    }, err => utils.toast('error', 'Unable to delete group', err.message))
  }

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : <Button colorPalette='blue' onClick={() => setOpen(true)}><Icon as={FaPlus} mr={2} /> Create a group</Button>}
      <SimpleModal
        isOpen={open} onClose={() => setOpen(false)} modalProps={{ size: 'lg' }}
        header={isEditing ? 'Update group' : 'Create a participant group'}
        body={
          <Box>
            <Text>Participant groups allow you to specify different arms, conditions, treatments, and more for your trial. For example, you may want a control group and a treatment group in order to compare the effect of a treatment.</Text>

            <Heading mt={5} size='sm'>Group name</Heading>
            <Text fontSize='sm'>Choose a name for your group. Participants cannot see their group(s).</Text>
            <Input mt={3} autoFocus type='text' placeholder='Group name' value={name} onChange={e => setName(e.target.value)} />

            <Heading mt={5} size='sm'>Group colour</Heading>
            <Text fontSize='sm'>A colour scheme helps you to identify groups of participants in the list.</Text>
            <Box display='flex' mt={3}>
              {groupColours.map(c =>
                <Box rounded='md' cursor='pointer' mr={2} w={10} h={10} key={c} bg={c} position='relative' onClick={() => setColour(c)}>
                  {c === colour &&
                    <Icon as={FaCheckCircle} position='absolute' style={{ top: -5, right: -5 }} />}
                </Box>
              )}
            </Box>
          </Box>
}
        footer={
          <Stack direction='row' justify='space-between'>
            {isEditing &&
              <ConfirmButton
                trigger={<Button variant='ghost'><Icon><FaTrash /></Icon></Button>}
                header='Really delete this group?'
                footer='Participants will be removed from the group, but the participants themselves will not be deleted.'
                onConfirm={e => onDeleteGroup(group?._id)}
              />}
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button colorPalette='primary' onClick={createGroup}>{isEditing ? 'Save group' : 'Add group'}</Button>
          </Stack>
}
      />
    </>
  )
}
